import React, { useState, useEffect } from 'react';
import { 
  Container,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  MenuItem,
  useMediaQuery,
  useTheme,
  TablePagination
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import axios from 'axios';

const UserManagementPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    correo: '',
    contraseña: '',
    confirmarContraseña: '',
    perfil_id: 2
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    fetchUsers();
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get('https://segesppiura.com:5003/api/perfiles');
      setProfiles(response.data);
    } catch (err) {
      setError('Error al cargar perfiles');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://segesppiura.com:5003/api/usuarios');
      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.contraseña !== formData.confirmarContraseña) {
      setError("Las contraseñas no coinciden");
      return;
    }

    try {
      setLoading(true);
      const url = editingUser 
        ? `https://segesppiura.com:5003/api/usuarios/${editingUser.id}` 
        : "https://segesppiura.com:5003/api/registro";
      
      const method = editingUser ? "put" : "post";

      await axios[method](url, {
        nombre: formData.nombre,
        correo: formData.correo,
        contraseña: formData.contraseña,
        perfil_id: formData.perfil_id,
      });

      fetchUsers();
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setEditingUser(null);
    setOpenDialog(true);
    setError('');
    setFormData({
      nombre: '',
      correo: '',
      contraseña: '',
      confirmarContraseña: '',
      perfil_id: 2
    });
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      nombre: user.nombre,
      correo: user.correo,
      contraseña: "",
      confirmarContraseña: "",
      perfil_id: user.perfil_id,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (userId) => {
    try {
      setLoading(true);
      await axios.delete(`https://segesppiura.com:5003/api/usuarios/${userId}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al eliminar usuario");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4 }}>
      <Box sx={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0, 150, 200, 0.1)',
        p: isMobile ? 2 : 4,
        border: '1px solid rgba(200, 230, 255, 0.5)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          mb: 4,
          borderBottom: '1px solid rgba(0, 180, 216, 0.2)',
          pb: 2,
          gap: isMobile ? 2 : 0
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"}
            component="h1"
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(to right, #0077b6, #00b4d8)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Gestión de Usuarios
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={handleOpenDialog}
            sx={{
              background: 'linear-gradient(to right, #0096c7, #00b4d8)',
              boxShadow: '0 2px 10px rgba(0, 180, 216, 0.3)',
              '&:hover': {
                background: 'linear-gradient(to right, #0077b6, #0096c7)',
              },
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Nuevo Usuario
          </Button>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#00b4d8' }} />
          </Box>
        ) : (
          <>
            <TableContainer 
              component={Paper}
              sx={{
                boxShadow: '0 2px 10px rgba(0, 150, 200, 0.1)',
                borderRadius: 2,
                overflow: 'auto'
              }}
            >
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5fbff' }}>
                    {!isMobile && <TableCell sx={{ fontWeight: 'bold', color: '#0077b6' }}>ID</TableCell>}
                    <TableCell sx={{ fontWeight: 'bold', color: '#0077b6' }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#0077b6' }}>Correo</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 'bold', color: '#0077b6' }}>Perfil</TableCell>}
                    <TableCell sx={{ fontWeight: 'bold', color: '#0077b6' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                    <TableRow 
                      key={user.id}
                      hover
                      sx={{ 
                        '&:nth-of-type(even)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f9ff' }
                      }}
                    >
                      {!isMobile && <TableCell>{user.id}</TableCell>}
                      <TableCell>{user.nombre}</TableCell>
                      <TableCell>{user.correo}</TableCell>
                      {!isMobile && (
                        <TableCell sx={{ 
                          fontWeight: user.perfil_id === 1 ? 'bold' : 'normal',
                          color: user.perfil_id === 1 ? '#0077b6' : 'inherit'
                        }}>
                          {user.perfil?.nombre || `ID: ${user.perfil_id}`}
                        </TableCell>
                      )}
                      <TableCell>
                        <IconButton 
                          color="primary" 
                          onClick={() => handleEdit(user)}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(0, 180, 216, 0.1)'
                            }
                          }}
                        >
                          <Edit fontSize={isMobile ? "small" : "medium"} />
                        </IconButton>
                        <IconButton 
                          color="error" 
                          onClick={() => handleDelete(user.id)}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(255, 86, 86, 0.1)'
                            }
                          }}
                        >
                          <Delete fontSize={isMobile ? "small" : "medium"} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={users.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              sx={{ mt: 2 }}
            />
          </>
        )}
      </Box>

      {/* Diálogo para nuevo/editar usuario */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            boxShadow: '0 4px 20px rgba(0, 150, 200, 0.2)',
            width: isMobile ? '100%' : '500px',
            maxWidth: '100%',
            m: 0
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(to right, #0077b6, #00b4d8)',
            color: 'white',
            fontWeight: 'bold',
            py: 2
          }}
        >
          {editingUser ? 'Editar Usuario' : 'Registrar Nuevo Usuario'}
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Nombre Completo"
              name="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Correo Electrónico"
              name="correo"
              type="email"
              value={formData.correo}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              margin="normal"
              required={!editingUser}
              fullWidth
              label="Contraseña"
              name="contraseña"
              type="password"
              value={formData.contraseña}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              margin="normal"
              required={!editingUser}
              fullWidth
              label="Confirmar Contraseña"
              name="confirmarContraseña"
              type="password"
              value={formData.confirmarContraseña}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              select
              margin="normal"
              required
              fullWidth
              label="Perfil"
              name="perfil_id"
              value={formData.perfil_id}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              size={isMobile ? "small" : "medium"}
            >
              {profiles.map((profile) => (
                <MenuItem 
                  key={profile.id} 
                  value={profile.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(0, 180, 216, 0.1)'
                    }
                  }}
                >
                  {profile.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              color: '#0077b6',
              '&:hover': {
                backgroundColor: 'rgba(0, 180, 216, 0.1)'
              },
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
            sx={{
              background: 'linear-gradient(to right, #0096c7, #00b4d8)',
              boxShadow: '0 2px 10px rgba(0, 180, 216, 0.3)',
              '&:hover': {
                background: 'linear-gradient(to right, #0077b6, #0096c7)',
              },
              '&:disabled': {
                background: '#e0e0e0'
              },
              width: isMobile ? '100%' : 'auto'
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : editingUser ? "Actualizar" : "Registrar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagementPage;